/* Metric camera calibration for an uncropped photograph of a level rectangle.
 * No sensor readings or fixed phone field of view. Coordinates are metres.
 * Input corners: near left, near right, far right, far left. */
(function(root){
  'use strict';
  const dot=(a,b)=>a.reduce((s,v,i)=>s+v*b[i],0);
  const norm=a=>Math.sqrt(dot(a,a));
  const unit=a=>a.map(v=>v/norm(a));
  const cross=(a,b)=>[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];
  function solve(A,b){
    A=A.map((r,i)=>[...r,b[i]]);const n=b.length;
    for(let k=0;k<n;k++){
      let p=k;for(let i=k+1;i<n;i++)if(Math.abs(A[i][k])>Math.abs(A[p][k]))p=i;
      if(Math.abs(A[p][k])<1e-11)throw Error('These corners are too close together. Choose a larger rectangle.');
      [A[p],A[k]]=[A[k],A[p]];const v=A[k][k];for(let j=k;j<=n;j++)A[k][j]/=v;
      for(let i=0;i<n;i++)if(i!==k){const d=A[i][k];for(let j=k;j<=n;j++)A[i][j]-=d*A[k][j]}
    }return A.map(r=>r[n]);
  }
  function homography(points,w,d){
    const world=[[0,0],[w,0],[w,d],[0,d]],A=[],b=[];
    world.forEach(([x,z],i)=>{const[u,v]=points[i];A.push([x,z,1,0,0,0,-u*x,-u*z],[0,0,0,x,z,1,-v*x,-v*z]);b.push(u,v)});
    return [...solve(A,b),1];
  }
  function project(P,x,y,z){const q=[x,y,z,1],depth=dot(P[2],q);return {x:dot(P[0],q)/depth,y:dot(P[1],q)/depth,depth}}
  function fit(points,w,d,W,H,focal){
    const h=homography(points,w,d),cx=W/2,cy=H/2;
    const a=h[0]-cx*h[6],b=h[3]-cy*h[6],c=h[1]-cx*h[7],e=h[4]-cy*h[7],g=h[6],j=h[7];
    const aa=g*j,ab=g*g-j*j,ba=-(a*c+b*e),bb=-(a*a+b*b-c*c-e*e),den=aa*aa+ab*ab;
    if(den<1e-13)throw Error('This view is too straight down to recover perspective. Take a photo looking across the rectangle.');
    const f2=(aa*ba+ab*bb)/den;
    if(!focal&&f2<=0)throw Error('The corners and measurements do not agree. Check their order and use a true rectangle on level ground.');
    const f=focal||Math.sqrt(f2),fov=2*Math.atan(Math.max(W,H)/2/f)*180/Math.PI;
    if(fov<20||fov>105)throw Error('Perspective could not be confirmed. Check the measurements and corners, or use a new uncropped 1× photo.');
    const x=[a/f,b/f,g],z=[c/f,e/f,j],scale=2/(norm(x)+norm(z)),rx=unit(x),rz=unit(z.map((v,i)=>v-dot(z,rx)*rx[i])),up=cross(rx,rz);
    const t=[(h[2]-cx)/f,(h[5]-cy)/f,1].map(v=>v*scale);
    const R=[rx,up,rz],P=[[],[],[]];
    for(let k=0;k<3;k++){P[0].push(f*R[k][0]+cx*R[k][2]);P[1].push(f*R[k][1]+cy*R[k][2]);P[2].push(R[k][2])}
    P[0].push(f*t[0]+cx*t[2]);P[1].push(f*t[1]+cy*t[2]);P[2].push(t[2]);
    const cameraHeight=-dot(up,t),world=[[0,0],[w,0],[w,d],[0,d]];
    const error=Math.sqrt(world.reduce((s,[x,z],i)=>{const q=project(P,x,0,z);return s+(q.x-points[i][0])**2+(q.y-points[i][1])**2},0)/4);
    return {P,f,fov,cameraHeight,error,w,d,W,H};
  }

  // Fit a rigid, measured rectangle to imprecise taps. Only camera pose and
  // focal length vary; the user's width and depth are never optimization variables.
  function refine(points,w,d,W,H,seed){
    const cx=W/2,cy=H/2,world=[[0,0],[w,0],[w,d],[0,d]];
    let pose={f:seed.f,R:[0,1,2].map(k=>[(seed.P[0][k]-cx*seed.P[2][k])/seed.f,(seed.P[1][k]-cy*seed.P[2][k])/seed.f,seed.P[2][k]]),t:[(seed.P[0][3]-cx*seed.P[2][3])/seed.f,(seed.P[1][3]-cy*seed.P[2][3])/seed.f,seed.P[2][3]]};
    const rotate=(v,a)=>{const n=norm(a);if(n<1e-14)return [...v];const u=a.map(x=>x/n),c=Math.cos(n),s=Math.sin(n),uv=cross(u,v);return v.map((x,i)=>x*c+uv[i]*s+u[i]*dot(u,v)*(1-c))};
    const change=(p,v)=>({f:p.f*Math.exp(v[6]),R:p.R.map(r=>rotate(r,v.slice(0,3))),t:p.t.map((x,i)=>x+v[i+3])});
    const residual=p=>world.flatMap(([x,z],i)=>{const q=p.t.map((v,k)=>v+x*p.R[0][k]+z*p.R[2][k]);return q[2]>.01?[p.f*q[0]/q[2]+cx-points[i][0],p.f*q[1]/q[2]+cy-points[i][1]]:[1e8,1e8]});
    let r=residual(pose),cost=dot(r,r),lambda=.001;
    for(let iter=0;iter<65&&cost>1e-16;iter++){
      const J=Array.from({length:7},(_,k)=>{const v=Array(7).fill(0);v[k]=1e-6;const rr=residual(change(pose,v));return rr.map((x,i)=>(x-r[i])/1e-6)});
      const A=J.map((a,i)=>J.map((b,j)=>dot(a,b)+(i===j?lambda*Math.max(1,dot(a,a)):0)));
      let v;try{v=solve(A,J.map(a=>-dot(a,r)))}catch(_){break}
      const candidate=change(pose,v),rr=residual(candidate),next=dot(rr,rr),fov=2*Math.atan(Math.max(W,H)/2/candidate.f)*180/Math.PI;
      if(next<cost&&fov>=20&&fov<=105){pose=candidate;r=rr;if(cost-next<1e-12){cost=next;break}cost=next;lambda=Math.max(1e-9,lambda/3)}else lambda*=8;
      if(lambda>1e12)break;
    }
    const P=[[],[],[]];for(const v of [...pose.R,pose.t]){P[0].push(pose.f*v[0]+cx*v[2]);P[1].push(pose.f*v[1]+cy*v[2]);P[2].push(v[2])}
    const snappedPoints=world.map(([x,z])=>{const q=project(P,x,0,z);return[q.x,q.y]});
    return {P,f:pose.f,fov:2*Math.atan(Math.max(W,H)/2/pose.f)*180/Math.PI,cameraHeight:-dot(pose.R[1],pose.t),error:Math.sqrt(cost/4),snappedPoints,maxAdjustment:Math.max(...snappedPoints.map((p,i)=>Math.hypot(p[0]-points[i][0],p[1]-points[i][1]))),w,d,W,H};
  }
  function fitRectangle(points,w,d,W,H){
    const seeds=[];try{seeds.push(fit(points,w,d,W,H))}catch(_){}
    for(const degrees of[40,65,90])try{seeds.push(fit(points,w,d,W,H,Math.max(W,H)/2/Math.tan(degrees*Math.PI/360)))}catch(_){}
    const candidates=seeds.map(seed=>refine(points,w,d,W,H,seed)).filter(c=>Number.isFinite(c.error));
    if(!candidates.length)throw Error('We could not align these corners. Check their order or try a clearer photo.');
    return candidates.sort((a,b)=>a.error-b.error)[0];
  }
  function calibrate(points,w,d,W,H){
    if(!Array.isArray(points)||points.length!==4||![w,d,W,H,...points.flat()].every(Number.isFinite))throw Error('Add all four corners and both measurements first.');
    if(w<1||d<1||w>30||d>30)throw Error('Use a reference area between 3.3 and 98 feet on each side.');
    if(points.some(p=>p.length!==2||p[0]<0||p[0]>W||p[1]<0||p[1]>H))throw Error('Keep all four corners inside the photo.');
    const turns=points.map((p,i)=>{const q=points[(i+1)%4],r=points[(i+2)%4];return(q[0]-p[0])*(r[1]-q[1])-(q[1]-p[1])*(r[0]-q[0])});
    if(!turns.every(t=>t<-1))throw Error('Tap near left, near right, far right, then far left, going around the rectangle.');
    const area=Math.abs(points.reduce((s,p,i)=>{const q=points[(i+1)%4];return s+p[0]*q[1]-q[0]*p[1]},0))/2;
    if(area<W*H*.025)throw Error('The reference area is too small in this photo. Move closer so the four corners are easier to mark.');
    if((points[0][1]+points[1][1]-points[2][1]-points[3][1])/2<H*.065)throw Error('The rectangle looks too shallow. Raise the camera or move closer, and keep all four corners visible.');
    const c=fitRectangle(points,w,d,W,H);
    if(c.cameraHeight<.35||c.cameraHeight>3.5)throw Error('The estimated camera height is unusual. Check the corner order, measurements, and that the area is level.');
    if(c.maxAdjustment>Math.min(W,H)*.03||c.snappedPoints.some(p=>p[0]<0||p[0]>W||p[1]<0||p[1]>H))throw Error('These corners need more than a small adjustment. Check the markers and measurements, or try another photo.');
    let sensitivity=0;const delta=Math.max(1.5,Math.min(W,H)*.002);
    const reference=project(c.P,w/2,.56,d/2);
    for(let i=0;i<4;i++)for(let axis=0;axis<2;axis++)for(const sign of[-1,1]){
      const p=points.map(v=>[...v]);p[i][axis]+=delta*sign;
      try{const v=refine(p,w,d,W,H,c),q=project(v.P,w/2,.56,d/2);sensitivity=Math.max(sensitivity,Math.hypot(q.x-reference.x,q.y-reference.y));if(v.cameraHeight<.2||v.cameraHeight>4.5)sensitivity=Infinity}catch(_){sensitivity=Infinity}
    }
    if(sensitivity>Math.min(W,H)*.025)throw Error('Small changes to these corners change the scale too much. Use a larger, clearer rectangle or a different photo angle.');
    return {...c,sensitivity,reviewRequired:c.maxAdjustment>Math.min(W,H)*.006};
  }
  function groundAt(P,u,v){
    const A=[[P[0][0]-u*P[2][0],P[0][2]-u*P[2][2]],[P[1][0]-v*P[2][0],P[1][2]-v*P[2][2]]];
    try{return solve(A,[u*P[2][3]-P[0][3],v*P[2][3]-P[1][3]])}catch(_){return null}
  }
  function clipMatrix(P,W,H,near=.05,far=200){
    const a=(far+near)/(far-near),b=-2*far*near/(far-near);
    return [...P[0].map((v,i)=>2*v/W-P[2][i]),...P[1].map((v,i)=>P[2][i]-2*v/H),...P[2].map((v,i)=>a*v+(i===3?b:0)),...P[2]];
  }
  function constrain(x,z,angle,w,d,L=1.27,D=.8128){
    const a=angle*Math.PI/180,hx=(Math.abs(Math.cos(a))*L+Math.abs(Math.sin(a))*D)/2,hz=(Math.abs(Math.sin(a))*L+Math.abs(Math.cos(a))*D)/2;
    if(hx*2>w||hz*2>d)return null;
    return {x:Math.max(hx,Math.min(w-hx,x)),z:Math.max(hz,Math.min(d-hz,z)),angle};
  }
  const api={calibrate,fit,fitRectangle,project,groundAt,clipMatrix,constrain};
  if(typeof module!=='undefined')module.exports=api;else root.YardGeometry=api;
})(typeof window!=='undefined'?window:globalThis);
